import React from 'react';
import {
  IconButton,
  Tooltip,
  Chip,
  Box,
  Typography,
  Divider
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  Stop as StopIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useImpersonation } from '../../contexts/ImpersonationContext';

const ImpersonationToggle: React.FC = () => {
  const { isImpersonating, originalUser, impersonatedUser, stopImpersonation, canManageUsers } = useImpersonation();
  const navigate = useNavigate();

  // Invariant: the Stop button must always be reachable during an active impersonation session,
  // regardless of whether the impersonated user has manage-users access.
  // Only hide the toggle entirely when not impersonating AND the user can't manage users.
  if (!canManageUsers && !isImpersonating) {
    return null;
  }

  if (isImpersonating && originalUser && impersonatedUser) {
    const impersonationTooltip = (
      <Box sx={{ p: 2, maxWidth: 300 }}>
        <Typography variant="h6" color="warning.main" sx={{ mb: 1 }}>
          🎭 Admin Impersonation Active
        </Typography>
        
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            <strong>You are viewing as:</strong>
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PersonIcon fontSize="small" />
            <Typography variant="body2">
              {impersonatedUser.firstName || impersonatedUser.first_name} {impersonatedUser.lastName || impersonatedUser.last_name}
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary">
            ({impersonatedUser.email})
          </Typography>
        </Box>

        <Divider sx={{ my: 1 }} />
        
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            <strong>Original user:</strong>
          </Typography>
          <Typography variant="body2">
            {originalUser.firstName || originalUser.first_name} {originalUser.lastName || originalUser.last_name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            ({originalUser.email})
          </Typography>
        </Box>

        <Divider sx={{ my: 1 }} />
        
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
          You can see exactly what this user sees and has access to. 
          Click the stop button to return to your admin view.
        </Typography>
      </Box>
    );

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Tooltip 
          title={impersonationTooltip}
          arrow
          placement="bottom-end"
          componentsProps={{
            tooltip: {
              sx: {
                bgcolor: 'warning.light',
                color: 'warning.contrastText',
                '& .MuiTooltip-arrow': {
                  color: 'warning.light',
                },
              },
            },
          }}
        >
          <Chip
            icon={<PersonIcon />}
            label={`As: ${impersonatedUser.firstName || impersonatedUser.first_name} ${impersonatedUser.lastName || impersonatedUser.last_name}`}
            color="warning"
            variant="outlined"
            size="small"
            sx={{ 
              maxWidth: 200,
              cursor: 'pointer',
              '& .MuiChip-label': {
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }
            }}
          />
        </Tooltip>
        
        <Tooltip title={`Stop impersonating ${impersonatedUser.firstName || impersonatedUser.first_name} ${impersonatedUser.lastName || impersonatedUser.last_name}`}>
          <IconButton
            onClick={stopImpersonation}
            color="warning"
            size="small"
            sx={{ 
              backgroundColor: 'rgba(255, 152, 0, 0.1)',
              '&:hover': {
                backgroundColor: 'rgba(255, 152, 0, 0.2)',
              }
            }}
          >
            <StopIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    );
  }

  // Show impersonation icon when not impersonating - clicking navigates to Users page
  const handleNavigateToUsers = () => {
    navigate('/users');
  };

  return (
    <Tooltip title="Click to go to Users page for impersonation">
      <IconButton
        onClick={handleNavigateToUsers}
        color="inherit"
        size="small"
        sx={{ 
          opacity: 0.6,
          '&:hover': {
            opacity: 1,
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
          }
        }}
      >
        <VisibilityIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );
};

export default ImpersonationToggle;
